import { sendEventToLsq, getDeviceAttributes, normalizeModuleEnv, } from "@oneapp/data";
import { administrativeDivisions, normalizeStateRegionForLsq, } from "./administrativeDivisions";
import { ActivityFieldsEventBuilder, LeadDetailsBuilder } from "./builders";
import { unified_new_lsq_config, unified_new_lsq_config_debug, } from "./staticLsqJson";
import { trackingSourceList } from "./trackingSourceList";
import { fetchGeniusSchoolCategoryDetails } from "./fetchGeniusSchoolCategoryDetails";
import { fetchGeoForLsqLead } from "./fetchGeoForLsqLead";
import { readGeniusLsqAccessToken, readGeniusSchoolDetailsSession, } from "./geniusSchoolDetailsSession";
import { configureOneGeniusApiBaseUrl, readBranchEnv, } from "./getOneGeniusApiBaseUrl";
const FULL_NAME_REGEX = /^[A-Za-z.]+$/;
let initOptions = null;
export let remoteData = null;
let deviceAttributesCache = null;
let cachedLeadGeo = null;
let leadGeoLoadPromise = null;
function hasUsableLeadGeo(geo) {
    if (!geo || typeof geo !== "object")
        return false;
    const ip = typeof geo.ip === "string" ? geo.ip.trim() : "";
    const region = typeof geo.region === "string" ? geo.region.trim() : "";
    const city = typeof geo.city === "string" ? geo.city.trim() : "";
    const country = typeof geo.country === "string" ? geo.country.trim() : "";
    const latitude = typeof geo.latitude === "string" ? geo.latitude.trim() : "";
    const longitude = typeof geo.longitude === "string" ? geo.longitude.trim() : "";
    return Boolean(ip || region || city || country || latitude || longitude);
}
function ensureLeadGeoCached() {
    if (hasUsableLeadGeo(cachedLeadGeo)) {
        return Promise.resolve(cachedLeadGeo);
    }
    if (!leadGeoLoadPromise) {
        leadGeoLoadPromise = fetchGeoForLsqLead()
            .then((geo) => {
            if (hasUsableLeadGeo(geo)) {
                cachedLeadGeo = geo;
            }
            else {
                cachedLeadGeo = null;
            }
            return geo;
        })
            .finally(() => {
            leadGeoLoadPromise = null;
        });
    }
    return leadGeoLoadPromise;
}
export function resolveLsqServicePlatform() {
    const explicit = initOptions?.getLsqServicePlatform?.();
    if (explicit)
        return explicit;
    const p = initOptions?.defaultPlatform;
    if (p === "Web" || p === "MWeb")
        return "web";
    return "android";
}
async function prefetchDeviceAttributes() {
    try {
        const platform = resolveLsqServicePlatform();
        if (platform === "web") {
            deviceAttributesCache = null;
            return;
        }
        const model = initOptions?.getDeviceModel?.()?.trim();
        const clientName = initOptions?.lsqServiceClientName;
        if (!model || !clientName) {
            deviceAttributesCache = null;
            return;
        }
        deviceAttributesCache = await getDeviceAttributes({ model }, {
            clientName,
            headers: { platform },
        });
    }
    catch (e) {
        deviceAttributesCache = null;
        if (initOptions?.isDebug) {
            console.warn("[SqsEvents] device attributes:", e);
        }
    }
}
function filterName(name) {
    const t = name?.trim();
    if (!t || !FULL_NAME_REGEX.test(t))
        return undefined;
    return t;
}
function isValidFirstName(name) {
    if (!name?.trim())
        return undefined;
    const t = name.trim();
    if (!/^[A-Za-z .]+$/.test(t))
        return undefined;
    return t;
}
function getLoginUserSafe() {
    return initOptions?.getLoginUser() ?? null;
}
function programGradeNumber() {
    const g = getLoginUserSafe()?.programGradeNumber;
    return g ?? undefined;
}
function fullName() {
    const u = getLoginUserSafe();
    if (!u)
        return undefined;
    const parts = [filterName(u.firstName), filterName(u.lastName)].filter(Boolean);
    const joined = parts.join(" ").trim();
    return joined.length > 0 ? joined : undefined;
}
function phone() {
    const u = getLoginUserSafe();
    if (!u)
        return undefined;
    const code = u.isdCode?.trim().replace(/^\+/, "") ?? "";
    const number = u.phone?.trim() ?? "";
    if (!code || !number)
        return undefined;
    return `+${code}-${number}`;
}
function resolveProspectId() {
    const id = getLoginUserSafe()?.prospectId?.trim();
    return id || undefined;
}
/** Genius deeplink LSQ: no URL field may exceed 199 chars. */
const GENIUS_LSQ_URL_MAX_LEN = 199;
function stripTokenFromUrl(url) {
    try {
        const parsed = new URL(url);
        parsed.searchParams.delete("token");
        return parsed.toString();
    }
    catch {
        // Relative / malformed: strip token query param manually.
        return url.replace(/([?&])token=[^&]*&?/i, "$1").replace(/[?&]$/, "");
    }
}
function truncateGeniusLsqUrl(url) {
    const trimmed = url?.trim();
    if (!trimmed)
        return undefined;
    const withoutToken = stripTokenFromUrl(trimmed);
    return withoutToken.length > GENIUS_LSQ_URL_MAX_LEN
        ? withoutToken.slice(0, GENIUS_LSQ_URL_MAX_LEN)
        : withoutToken;
}
function unifiedAccountId() {
    const id = getLoginUserSafe()?.unifiedAccountId?.trim();
    return id || undefined;
}
function userType() {
    const isUserTypeFree = initOptions?.getIsUserTypeFree() ?? true;
    const isPackageAmountMoreThenThousand = initOptions?.getIsPackageAmountMoreThenThousand() ?? false;
    return isUserTypeFree && !isPackageAmountMoreThenThousand ? "Free" : "Paid";
}
function loadConfigSafely() {
    if (!initOptions)
        return false;
    try {
        // Mirrors Android:
        // val key = appContext.getString(
        //     if (BuildConfig.DEBUG) R.string.unified_new_lsq_config_debug
        //     else R.string.unified_new_lsq_config)
        // val rawConfig = firebaseRemoteConfig.getString(key)
        const key = initOptions.isDebug
            ? "unified_new_lsq_config_debug"
            : "unified_new_lsq_config";
        const rawConfig = key === "unified_new_lsq_config_debug"
            ? unified_new_lsq_config_debug
            : unified_new_lsq_config;
        remoteData = JSON.parse(rawConfig);
        return remoteData != null;
    }
    catch {
        remoteData = null;
        return false;
    }
}
function requireRemoteConfig() {
    if (remoteData != null)
        return true;
    return loadConfigSafely();
}
function formatUtcDateTimeFromIso(value) {
    if (value == null || value === "")
        return undefined;
    const d = new Date(value);
    if (Number.isNaN(d.getTime()))
        return value;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}
export function getUtcDateTime() {
    return formatUtcDateTimeFromIso(new Date().toISOString());
}
function getNetworkType() {
    try {
        return initOptions?.getNetworkType?.() ?? null;
    }
    catch {
        return null;
    }
}
function isLsqDisabled() {
    const user = getLoginUserSafe();
    if (!user)
        return false;
    const config = remoteData?.androidConfig ?? {
        verticalIds: [1],
        isdCode: ["91"],
        isTriggerForPaidUser: true,
    };
    const isLSQEnabled = remoteData?.isLSQEnabled ?? true;
    const verticalId = user.verticalId;
    const verticalMatch = verticalId != null && (config.verticalIds ?? []).includes(verticalId);
    console.log("config.verticalMatch", verticalMatch);
    console.log("config.verticalId", verticalId);
    console.log("config.verticalIds", config.verticalIds);
    const isdRaw = user.isdCode?.trim().replace(/^\+/, "") ?? "";
    const isdCode = isdRaw.length > 0 ? isdRaw : undefined;
    const isdCodeMatch = isdCode != null && (config.isdCode ?? []).includes(isdCode);
    return !(isLSQEnabled && verticalMatch && isdCodeMatch);
}
function isLsqDisableForUltimatePackage() {
    const isTrigger = remoteData?.androidConfig?.isTriggerForPaidUser ?? true;
    const hasTrigger = initOptions?.getHasTriggerLsqForSubscription() ?? false;
    return !isTrigger === hasTrigger;
}
function resolveStateForLead(state) {
    return normalizeStateRegionForLsq(state);
}
function readUnknownString(obj, keys) {
    if (!obj)
        return "";
    for (const k of keys) {
        const v = obj[k];
        if (typeof v === "string" && v.trim())
            return v.trim();
        if (typeof v === "number" && Number.isFinite(v))
            return String(v);
    }
    return "";
}
function applyDeviceAttributesCacheToAppFields(fields, cache) {
    if (!cache || typeof cache !== "object")
        return fields;
    const r = cache;
    const mfr = readUnknownString(r, [
        "manufacturer",
        "Manufacturer",
        "brand",
        "Brand",
        "mx_CustomObject_1",
    ]);
    const marketing = readUnknownString(r, [
        "marketing_name",
        "MarketingName",
        "device_marketing_name",
        "mx_CustomObject_2",
    ]);
    const alt = readUnknownString(r, [
        "alternate_model",
        "model_secondary",
        "secondary_model",
        "mx_CustomObject_3",
    ]);
    return fields.map((f) => {
        if (f.Attribute === "mx_CustomObject_1" && mfr)
            return { ...f, Value: mfr };
        if (f.Attribute === "mx_CustomObject_2" && marketing)
            return { ...f, Value: marketing };
        if (f.Attribute === "mx_CustomObject_3" && alt)
            return { ...f, Value: alt };
        return f;
    });
}
function buildAppMxMobileDeviceSubFieldsFromCacheOnly(cache) {
    if (!cache || typeof cache !== "object")
        return [];
    const r = cache;
    const rows = [];
    for (let i = 1; i <= 5; i += 1) {
        const key = `mx_CustomObject_${i}`;
        rows.push({ Attribute: key, Value: readUnknownString(r, [key]) });
    }
    if (rows.every((x) => !x.Value))
        return [];
    return rows;
}
/** DOM-free access so consumers (e.g. React Native) without `lib: DOM` can typecheck. */
function readGlobalNavigatorUserAgent() {
    const nav = globalThis.navigator;
    const ua = nav?.userAgent;
    return typeof ua === "string" && ua.length > 0 ? ua : undefined;
}
function resolveWebMxMobileDeviceSubFields() {
    const isMweb = initOptions?.defaultPlatform === "MWeb" ||
        initOptions?.isMobileWebViewport?.() === true;
    const ua = readGlobalNavigatorUserAgent();
    if (ua === undefined) {
        return isMweb
            ? [
                { Attribute: "mx_CustomObject_4", Value: "Unknown" },
                { Attribute: "mx_CustomObject_5", Value: "Mobile" },
            ]
            : [
                { Attribute: "mx_CustomObject_4", Value: "Windows" },
                { Attribute: "mx_CustomObject_5", Value: "Desktop" },
            ];
    }
    if (isMweb) {
        if (/iPhone|iPad|iPod/i.test(ua)) {
            return [
                { Attribute: "mx_CustomObject_4", Value: "iOS" },
                { Attribute: "mx_CustomObject_5", Value: "Mobile" },
            ];
        }
        if (/Android/i.test(ua)) {
            return [
                { Attribute: "mx_CustomObject_4", Value: "Android" },
                { Attribute: "mx_CustomObject_5", Value: "Mobile" },
            ];
        }
        return [
            { Attribute: "mx_CustomObject_4", Value: "Mobile Web" },
            { Attribute: "mx_CustomObject_5", Value: "Mobile" },
        ];
    }
    if (/Mac OS X|Macintosh/i.test(ua)) {
        return [
            { Attribute: "mx_CustomObject_4", Value: "macOS" },
            { Attribute: "mx_CustomObject_5", Value: "Desktop" },
        ];
    }
    if (/Windows/i.test(ua)) {
        return [
            { Attribute: "mx_CustomObject_4", Value: "Windows" },
            { Attribute: "mx_CustomObject_5", Value: "Desktop" },
        ];
    }
    if (/Linux/i.test(ua)) {
        return [
            { Attribute: "mx_CustomObject_4", Value: "Linux" },
            { Attribute: "mx_CustomObject_5", Value: "Desktop" },
        ];
    }
    return [
        { Attribute: "mx_CustomObject_4", Value: "Windows" },
        { Attribute: "mx_CustomObject_5", Value: "Desktop" },
    ];
}
function resolveActivityPlatformField(intentPlatform) {
    const trimmed = intentPlatform?.trim();
    if (trimmed)
        return trimmed;
    const def = initOptions?.defaultPlatform;
    if (def === "Web" && initOptions?.isMobileWebViewport?.() === true) {
        return "MWeb";
    }
    return def ?? "App";
}
function buildMxMobileDeviceSubFieldsForLead() {
    if (resolveLsqServicePlatform() === "web") {
        return resolveWebMxMobileDeviceSubFields();
    }
    const fromApp = initOptions?.getAppMxMobileDeviceFields?.() ?? null;
    if (fromApp?.length) {
        return applyDeviceAttributesCacheToAppFields(fromApp, deviceAttributesCache);
    }
    return buildAppMxMobileDeviceSubFieldsFromCacheOnly(deviceAttributesCache);
}
function createDefaultLeadIntent(override, rich) {
    const isWeb = resolveLsqServicePlatform() === "web" ||
        initOptions?.defaultPlatform === "Web" ||
        initOptions?.defaultPlatform === "MWeb";
    const productName = isWeb ? "IL Website" : "Learn (AN)";
    let productOrigin = isWeb
        ? trackingSourceList["Il_website"]
        : trackingSourceList["learn_an"];
    const trackingKey = initOptions?.getTrackingSourceKey?.()?.trim();
    if (trackingKey && trackingSourceList[trackingKey]) {
        productOrigin = trackingSourceList[trackingKey];
    }
    const phoneNumber = phone();
    const prospect = resolveProspectId();
    // No phone (e.g. username/admission login) → identify lead by prospect_id.
    const useProspectId = !phoneNumber && Boolean(prospect);
    const base = {
        searchBy: useProspectId ? "prospect_id" : "Phone",
        userPhoneNumber: useProspectId ? undefined : phoneNumber,
        ...(useProspectId && prospect ? { prospect_id: prospect } : {}),
        unifiedAccountID: unifiedAccountId(),
        academicGrade: programGradeNumber() ?? undefined,
        productOrigin,
        productName,
        networkType: getNetworkType() ?? undefined,
    };
    if (!rich) {
        return { ...base, ...override };
    }
    return {
        ...base,
        ...(cachedLeadGeo?.region
            ? {
                state: administrativeDivisions.filter((d) => d.name?.toLowerCase() === cachedLeadGeo?.region?.toLowerCase())[0]?.name ??
                    cachedLeadGeo?.region ??
                    undefined,
            }
            : {}),
        ...(cachedLeadGeo?.city ? { city: cachedLeadGeo.city } : {}),
        ...(cachedLeadGeo?.ip ? { sourceIpAddress: cachedLeadGeo.ip } : {}),
        ...(cachedLeadGeo?.latitude ? { latitude: cachedLeadGeo.latitude } : {}),
        ...(cachedLeadGeo?.longitude ? { longitude: cachedLeadGeo.longitude } : {}),
        ...override,
    };
}
function generateLead(leadIntent, attachMxMobileDeviceToLead, leadDetailMode) {
    if (!remoteData)
        return [];
    const schema = remoteData.schemas?.Lead;
    if (leadDetailMode === "minimal") {
        const local = createDefaultLeadIntent(leadIntent, false);
        const phoneNumber = local.userPhoneNumber?.trim() || phone();
        const prospect = !phoneNumber
            ? local.prospect_id?.trim() || resolveProspectId()
            : undefined;
        const searchBy = prospect ? "prospect_id" : (local.searchBy ?? "Phone");
        const builder = new LeadDetailsBuilder()
            .put(schema?.searchBy, searchBy)
            .put(schema?.userPhoneNumber, prospect ? undefined : local.userPhoneNumber)
            .put(schema?.unifiedAccountID, local.unifiedAccountID)
            .put(schema?.academicGrade, local.academicGrade)
            .put(schema?.productOrLeadOrigin, local.productOrigin)
            .put(schema?.productName, local.productName)
            .put(schema?.networkType, local.networkType);
        if (prospect) {
            builder.put(schema?.prospect_id ?? "prospect_id", prospect);
        }
        return builder.build();
    }
    const local = createDefaultLeadIntent(leadIntent ?? undefined, true);
    const phoneNumber = local.userPhoneNumber?.trim() || phone();
    const prospect = !phoneNumber
        ? local.prospect_id?.trim() || resolveProspectId()
        : undefined;
    const searchBy = prospect ? "prospect_id" : (local.searchBy ?? "Phone");
    const leadDetails = new LeadDetailsBuilder()
        .put(schema?.searchBy, searchBy)
        .put(schema?.userPhoneNumber, prospect ? undefined : local.userPhoneNumber)
        .put(schema?.userFullName, local.userFullName)
        .put(schema?.userEmail, local.userEmail)
        .put(schema?.unifiedAccountID, local.unifiedAccountID)
        .put(schema?.academicGrade, local.academicGrade)
        .put(schema?.productOrLeadOrigin, local.productOrigin)
        .put(schema?.utmCampaign, local.utmCampaign)
        .put(schema?.utmMedium, local.utmMedium)
        .put(schema?.utmTerm, local.utmTerm)
        .put(schema?.utmContent, local.utmContent)
        .put(schema?.referrerURL, local.referrerUrl)
        .put(schema?.pageURL, local.pageUrl)
        .put(schema?.state, resolveStateForLead(local.state))
        .put(schema?.city, local.city)
        .put(schema?.productName, local.productName)
        .put(schema?.sourceIpAddress, local.sourceIpAddress)
        .put(schema?.optInDate, local.optInDate)
        .put(schema?.networkType, local.networkType);
    if (prospect) {
        leadDetails.put(schema?.prospect_id ?? "prospect_id", prospect);
    }
    const shouldAttachMxMobileDevice = attachMxMobileDeviceToLead === true ||
        initOptions?.attachMxMobileDeviceToAllLeads === true;
    if (shouldAttachMxMobileDevice) {
        const deviceSubFields = buildMxMobileDeviceSubFieldsForLead();
        if (deviceSubFields.length) {
            leadDetails.putMxMobileDevice(schema?.deviceInfoObject, deviceSubFields);
        }
    }
    return leadDetails.build();
}
/** LSQ mx_Env_Staging_or_Production — production / preprod / everything else as staging. */
function resolveMxEnvStagingOrProduction() {
    const env = normalizeModuleEnv(initOptions?.getModuleEnv?.() ?? readBranchEnv());
    return env === "production" || env === "preprod" ? env : "staging";
}
/** Genius deeplink LSQ lead details — includes mx_Grade from session/profile. */
function generateGeniusLead(leadIntent) {
    if (!remoteData)
        return [];
    const schema = remoteData.schemas?.Lead;
    const session = readGeniusSchoolDetailsSession();
    const academicGrade = leadIntent?.academicGrade ??
        session?.academicGrade ??
        getLoginUserSafe()?.profileGradeNumber ??
        programGradeNumber() ??
        undefined;
    const local = {
        ...createDefaultLeadIntent(undefined, false),
        ...leadIntent,
        ...(academicGrade != null ? { academicGrade } : {}),
    };
    const prospectId = leadIntent?.prospect_id?.trim() ||
        local.prospect_id?.trim() ||
        session?.prospectId?.trim() ||
        resolveProspectId();
    const phoneNumber = local.userPhoneNumber?.trim();
    const isWeb = resolveLsqServicePlatform() === "web" ||
        initOptions?.defaultPlatform === "Web" ||
        initOptions?.defaultPlatform === "MWeb";
    const productName = isWeb ? "IL Website" : "Learn (AN)";
    const productOrigin = isWeb
        ? trackingSourceList["Il_website"]
        : trackingSourceList["learn_an"];
    const builder = new LeadDetailsBuilder()
        .put(schema?.mxEnvStagingOrProduction, resolveMxEnvStagingOrProduction())
        .put(schema?.unifiedAccountID, local.unifiedAccountID)
        .put(schema?.pageURL, truncateGeniusLsqUrl(local.pageUrl))
        .put(schema?.firstName, leadIntent?.firstName)
        .put(schema?.lastName, leadIntent?.lastName)
        .put(schema?.optInDate, leadIntent?.optInDate)
        .put(schema?.mxCertificate, truncateGeniusLsqUrl(leadIntent?.mxCertificate))
        .put(schema?.mxCarLink, truncateGeniusLsqUrl(leadIntent?.mxCarLink))
        .put(schema?.mxTestDate ?? "mx_Test_Date", leadIntent?.mxTestDate)
        .put(schema?.productName, productName)
        .put(schema?.productOrLeadOrigin, productOrigin)
        .put(schema?.mxStudentLoginID, leadIntent?.mxStudentLoginID)
        .put(schema?.academicGrade, academicGrade)
        .put(schema?.primaryTargetExam, leadIntent?.primaryTargetExam)
        .put(schema?.mxInmobiusSchoolCode, leadIntent?.mxInmobiusSchoolCode)
        .put(schema?.mxInmobiusSchoolAddress, leadIntent?.mxInmobiusSchoolAddress)
        .put(schema?.schoolName, leadIntent?.schoolName)
        .put(schema?.mxBoard, leadIntent?.mxBoard)
        .put(schema?.city, local.city?.trim() || cachedLeadGeo?.city?.trim() || undefined)
        .put(schema?.mxDistrict, leadIntent?.mxDistrict)
        .put(schema?.country, leadIntent?.country ?? cachedLeadGeo?.country)
        .put(schema?.centerPinCode, leadIntent?.centerPinCode)
        .put(schema?.state, resolveStateForLead(local.state?.trim() || cachedLeadGeo?.region?.trim() || undefined))
        .put(schema?.mxFees, leadIntent?.mxFees)
        .put(schema?.mxTier, leadIntent?.mxTier)
        .put(schema?.mxCategory, leadIntent?.mxCategory);
    if (prospectId) {
        builder.put(schema?.prospect_id ?? "prospect_id", prospectId);
        builder.put(schema?.searchBy, "prospect_id");
    }
    else {
        builder.put(schema?.userPhoneNumber, phoneNumber);
        builder.put(schema?.searchBy, "Phone");
    }
    // Genius lead: nested mx_device Fields (mx_CustomObject_4 / _5).
    const deviceSubFields = buildMxMobileDeviceSubFieldsForLead();
    if (deviceSubFields.length) {
        builder.putMxMobileDevice(schema?.mxDevice ?? schema?.deviceInfoObject ?? "mx_device", deviceSubFields);
    }
    return builder.build();
}
function mergeFlashcardIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        firstName: intent.firstName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        platform,
    };
}
export function resolveProductVerticalForVideo(intentProductVertical) {
    const map = remoteData?.productVerticalMap;
    if (!map)
        return undefined;
    const intentId = intentProductVertical?.trim();
    if (intentId) {
        const fromIntent = map[intentId]?.trim();
        if (fromIntent)
            return fromIntent;
    }
    const vid = getLoginUserSafe()?.verticalId;
    if (vid == null)
        return undefined;
    return map[String(vid)]?.trim() || undefined;
}
function mergeActSelfLearnVideoIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
async function emitActivity(eventId, fields, leadIntent, attachMxMobileDeviceToLead, leadDetailMode = "minimal") {
    if (!initOptions || eventId == null)
        return;
    await sendEventToLsq({
        LeadDetails: generateLead(leadIntent, attachMxMobileDeviceToLead, leadDetailMode),
        Activity: {
            ActivityEvent: String(eventId),
            ActivityDateTime: getUtcDateTime(),
            Fields: fields,
        },
    }, { clientName: initOptions.lsqManagementClientName });
}
/** Genius deeplink LSQ only — passes stored gateway token without changing other events. */
async function emitGeniusActivity(eventId, fields, leadIntent) {
    if (!initOptions || eventId == null)
        return;
    const leadDetails = generateGeniusLead(leadIntent);
    if (!leadDetails.length) {
        if (initOptions.isDebug) {
            console.warn("[SqsEvents] Genius LSQ skipped: no prospect_id or Phone");
        }
        return;
    }
    const geniusToken = readGeniusLsqAccessToken();
    const lsqPlatform = initOptions.getLsqServicePlatform?.() ?? "web";
    const requestOptions = geniusToken
        ? {
            clientName: initOptions.lsqManagementClientName,
            headers: {
                Authorization: geniusToken.startsWith("Bearer ")
                    ? geniusToken
                    : `Bearer ${geniusToken}`,
                platform: lsqPlatform,
            },
        }
        : { clientName: initOptions.lsqManagementClientName };
    console.log("emitGeniusActivity", fields);
    await sendEventToLsq({
        LeadDetails: leadDetails,
        Activity: {
            ActivityEvent: String(eventId),
            ActivityDateTime: getUtcDateTime(),
            Fields: fields,
        },
    }, requestOptions);
}
function mergeActSignUpIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
        state: administrativeDivisions.filter((d) => d.name?.toLowerCase() === cachedLeadGeo?.region?.toLowerCase())[0]?.name ??
            cachedLeadGeo?.region ??
            undefined,
        city: cachedLeadGeo?.city ?? undefined,
    };
}
function mergeActMarketingIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        platform,
        state: administrativeDivisions.filter((d) => d.name?.toLowerCase() === cachedLeadGeo?.region?.toLowerCase())[0]?.name ??
            cachedLeadGeo?.region ??
            undefined,
        city: cachedLeadGeo?.city ?? undefined,
    };
}
function mergeActTestIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        testName: intent.testName,
        examType: intent.examType,
        testMode: intent.testMode,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        platform,
        testDate: formatUtcDateTimeFromIso(intent.testDate) ?? intent.testDate,
    };
}
function mergeActMasterClassIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActSelfLearnStoryIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActTalkToExpertIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        phone: intent.phone,
        examName: intent.examName,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActDoubtIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        doubtsSubject: intent.subject,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActScoreRegistrationIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActAddToCartIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeGeniusUserLoginIntent(intent) {
    return {
        ...intent,
        loginUrl: truncateGeniusLsqUrl(intent.loginUrl),
        source: resolveProductVerticalForVideo(intent.source),
        ipAddress: intent.ipAddress ?? cachedLeadGeo?.ip ?? undefined,
        Latitude: intent.latitude ?? cachedLeadGeo?.latitude ?? undefined,
        Longitude: intent.Longitude ?? cachedLeadGeo?.longitude ?? undefined,
        utmSource: intent.utmSource ?? undefined,
        utmCampaign: intent.utmCampaign ?? undefined,
    };
}
function mergeInMobiusOnlineTestIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    const grade = intent.grade ??
        (programGradeNumber() != null ? String(programGradeNumber()) : undefined);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        grade,
        platform,
        source: resolveProductVerticalForVideo(intent.source),
        ipAddress: intent.ipAddress ?? cachedLeadGeo?.ip ?? undefined,
        country: intent.country ?? cachedLeadGeo?.country ?? undefined,
        reportUrl: truncateGeniusLsqUrl(intent.reportUrl),
        certificateUrl: truncateGeniusLsqUrl(intent.certificateUrl),
    };
}
function mergeGeniusPhoneLinkIntent(intent) {
    return {
        ...intent,
        loginUrl: truncateGeniusLsqUrl(intent.loginUrl),
        source: resolveProductVerticalForVideo(intent.source),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
    };
}
function init(options) {
    initOptions = options;
    configureOneGeniusApiBaseUrl({
        getOneGeniusApiBaseUrl: options.getOneGeniusApiBaseUrl,
        getModuleEnv: options.getModuleEnv,
    });
    loadConfigSafely();
    void prefetchDeviceAttributes();
    void ensureLeadGeoCached();
}
function actSelfLearnFlashcard(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Self_Learn_Flashcards;
            const merged = mergeFlashcardIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.flashcard_subject, merged.flashcardSubject)
                .put(schema?.flashcard_chapter, merged.flashcardChapter)
                .put(schema?.firstName, isValidFirstName(merged.firstName))
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountID, merged.unifiedAccountID)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Self_Learn_Flashcards;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actSelfLearnFlashcard:", e);
            }
        }
    })();
}
function actSelfLearnVideo(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Self_Learn_Video;
            const merged = mergeActSelfLearnVideoIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.studyVideo, merged.studyVideo)
                .put(schema?.durationWatched, merged.durationWatched)
                .put(schema?.subject, merged.subject)
                .put(schema?.chapter, merged.chapter)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Self_Learn_Video;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actSelfLearnVideo:", e);
            }
        }
    })();
}
function actSelfLearnStory(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Self_Learn_Story;
            const merged = mergeActSelfLearnStoryIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.story_subject, merged.subject)
                .put(schema?.story_chapter, merged.chapter)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountID, merged.unifiedAccountID)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Self_Learn_Story;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actSelfLearnStory:", e);
            }
        }
    })();
}
function actMarketingActivity(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            await ensureLeadGeoCached();
            const schema = remoteData.schemas?.ACT_marketing;
            const merged = mergeActMarketingIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.utmUrl, merged.utmUrl)
                .put(schema?.utmSource, merged.utmSource)
                .put(schema?.utmCampaign, merged.utmCampaign)
                .put(schema?.utmMedium, merged.utmMedium)
                .put(schema?.utmContent, merged.utmContent)
                .put(schema?.utmId, merged.utmId)
                .put(schema?.utmTerm, merged.utmTerm)
                .put(schema?.grade, merged.grade)
                .put(schema?.examName, merged.examName)
                .put(schema?.referralPage, merged.referralPage)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.parentOrStudent, merged.parentOrStudent)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountID)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ACT_marketing;
            await emitActivity(eventId, jsonObject, leadIntent, true, "marketingSignup");
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actMarketingActivity:", e);
            }
        }
    })();
}
function actTests(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Tests;
            const merged = mergeActTestIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.tests_exam_type, merged.examType)
                .put(schema?.tests_test_name, merged.testName)
                .put(schema?.tests_score, merged.score)
                .put(schema?.percentage, merged.percentage)
                .put(schema?.tests_rank, merged.rank)
                .put(schema?.tests_subject, merged.subjects)
                .put(schema?.userType, merged.userType)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.testMode, merged.testMode)
                .put(schema?.testDate, merged.testDate)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.unifiedAccountID, merged.unifiedAccountID)
                .put(schema?.reportURL, merged.reportURL)
                .build();
            const eventId = remoteData.eventIds?.Act_Tests;
            if (readGeniusLsqAccessToken()) {
                await emitGeniusActivity(eventId, jsonObject, leadIntent);
            }
            else {
                await emitActivity(eventId, jsonObject, leadIntent);
            }
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actTests:", e);
            }
        }
    })();
}
function actMasterClass(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Master_Class_App;
            const merged = mergeActMasterClassIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.recorded_class, merged.recordedClass)
                .put(schema?.className, merged.className)
                .put(schema?.watchTimeInSeconds, merged.watchTimeInSeconds)
                .put(schema?.subject, merged.subject)
                .put(schema?.facultyName, merged.facultyName)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountID, merged.unifiedAccountID)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Master_Class_App;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actMasterClass:", e);
            }
        }
    })();
}
function actTalkToExpertActivity(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            const schema = remoteData.schemas?.ACT_TALK_TO_EXPERT;
            const merged = mergeActTalkToExpertIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.requestedDate, merged.requestedDate)
                .put(schema?.phone, merged.phone)
                .put(schema?.preferableDateTime, merged.preferableDateTime)
                .put(schema?.attendee, merged.attendee)
                .put(schema?.language, merged.language)
                .put(schema?.remarks, merged.remarks)
                .put(schema?.decisionMaker, merged.decisionMaker)
                .put(schema?.examName, merged.examName)
                .put(schema?.grade, merged.grade)
                .put(schema?.parentOrStudent, merged.parentOrStudent)
                .put(schema?.intentOfTheStudent, merged.intentOfTheStudent)
                .put(schema?.interestInOnlineCoaching, merged.interestInOnlineCoaching)
                .put(schema?.howDoYouStudyOnline, merged.howDoYouStudyOnline)
                .put(schema?.difficultSubjectForStudent, merged.difficultSubjectForStudent)
                .put(schema?.preferredLearningMode, merged.preferredLearningMode)
                .put(schema?.schoolName, merged.schoolName)
                .put(schema?.inMobiusSchoolCode, merged.inMobiusSchoolCode)
                .put(schema?.latitudeLong, merged.latitudeLong)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ACT_TALK_TO_EXPERT;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actTalkToExpertActivity:", e);
            }
        }
    })();
}
function actDoubtActivity(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Doubts;
            const merged = mergeActDoubtIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.doubtsSubject, merged.subject)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Doubts;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actDoubtActivity:", e);
            }
        }
    })();
}
function actSignUp(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            await ensureLeadGeoCached();
            const schema = remoteData.schemas?.ACT_SignUp;
            const merged = mergeActSignUpIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.grade, merged.grade)
                .put(schema?.examName, merged.examName)
                .put(schema?.state, merged.state)
                .put(schema?.city, merged.city)
                .put(schema?.utmUrl, merged.utmUrl)
                .put(schema?.utmSource, merged.utmSource)
                .put(schema?.utmCampaign, merged.utmCampaign)
                .put(schema?.utmMedium, merged.utmMedium)
                .put(schema?.utmContent, merged.utmContent)
                .put(schema?.utmId, merged.utmId)
                .put(schema?.utmTerm, merged.utmTerm)
                .put(schema?.referralPage, merged.referralPage)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.parentOrStudent, merged.parentOrStudent)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ACT_SignUp;
            await emitActivity(eventId, jsonObject, leadIntent, true, "marketingSignup");
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actSignUp:", e);
            }
        }
    })();
}
function appScoreRegistration(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            const schema = remoteData.schemas?.ActScoreRegistration;
            const merged = mergeActScoreRegistrationIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.grade, merged.grade)
                .put(schema?.examName, merged.examName)
                .put(schema?.testName, merged.testName)
                .put(schema?.utmUrl, merged.utmUrl)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ActScoreRegistration;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] appScoreRegistration:", e);
            }
        }
    })();
}
function appAddToCart(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            const schema = remoteData.schemas?.ActAddToCart;
            const merged = mergeActAddToCartIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.packageAmountRs, merged.packageAmountRs)
                .put(schema?.discountAmountRs, merged.discountAmountRs)
                .put(schema?.orderAmountRs, merged.orderAmountRs)
                .put(schema?.orderDateTime, merged.orderDateTime)
                .put(schema?.paymentPlatform, merged.paymentPlatform)
                .put(schema?.couponCode, merged.couponCode)
                .put(schema?.orderId, merged.orderId)
                .put(schema?.packageName, merged.packageName)
                .put(schema?.packageValidity, merged.packageValidity)
                .put(schema?.billingPhoneNumber, merged.billingPhoneNumber)
                .put(schema?.billingName, merged.billingName)
                .put(schema?.billingEmail, merged.billingEmail)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ActAddToCart;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] appAddToCart:", e);
            }
        }
    })();
}
function geniusUserLogin(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            await ensureLeadGeoCached();
            const schema = remoteData.schemas?.GeniusUserLoginEventId;
            const merged = mergeGeniusUserLoginIntent(intent);
            console.log("merged", merged);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.loginUrl, merged.loginUrl)
                .put(schema?.source, merged.source)
                .put(schema?.latitude, merged.Latitude)
                .put(schema?.longitude, merged.Longitude)
                .put(schema?.ipAddress, merged.ipAddress)
                .put(schema?.utmSource ?? "mx_Custom_7", merged.utmSource)
                .put(schema?.utmCampaign ?? "mx_Custom_8", merged.utmCampaign)
                .build();
            const eventId = remoteData.eventIds?.GeniusUserLoginEventId;
            const localLead = {
                ...leadIntent,
                optInDate: getUtcDateTime(),
                // mx_Actual_source_URL — Genius deeplink / login URL
                pageUrl: merged.loginUrl ||
                    truncateGeniusLsqUrl(leadIntent?.pageUrl) ||
                    undefined,
            };
            console.log("jsonObject", jsonObject);
            await emitGeniusActivity(eventId, jsonObject, localLead);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] geniusUserLogin:", e);
            }
        }
    })();
}
function inMobiusOnlineTest(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const leadGeo = await ensureLeadGeoCached();
            const schoolSession = readGeniusSchoolDetailsSession();
            const schoolDetails = schoolSession?.schoolDetails;
            const enrichedIntent = {
                ...intent,
                schoolName: intent.schoolName?.trim() ||
                    schoolDetails?.school_name?.trim() ||
                    undefined,
                district: intent.district?.trim() ||
                    schoolDetails?.district_name?.trim() ||
                    undefined,
                city: intent.city?.trim() ||
                    leadGeo?.city?.trim() ||
                    cachedLeadGeo?.city?.trim() ||
                    undefined,
                state: intent.state?.trim() ||
                    leadGeo?.region?.trim() ||
                    cachedLeadGeo?.region?.trim() ||
                    undefined,
                country: intent.country?.trim() ||
                    leadGeo?.country?.trim() ||
                    cachedLeadGeo?.country?.trim() ||
                    undefined,
                ipAddress: intent.ipAddress?.trim() ||
                    leadGeo?.ip?.trim() ||
                    cachedLeadGeo?.ip?.trim() ||
                    undefined,
                username: intent.username ?? undefined,
                certificateUrl: intent.certificateUrl ?? undefined,
                reportUrl: intent.reportUrl ?? undefined,
                marksObtained: intent.marksObtained ?? undefined,
                totalMarks: intent.totalMarks ?? undefined,
                percentage: intent.percentage ?? undefined,
                correctAnswers: intent.correctAnswers ?? undefined,
                incorrectAnswers: intent.incorrectAnswers ?? undefined,
                questionsAnswered: intent.questionsAnswered ?? undefined,
                testDate: intent.testDate ?? undefined,
                repName: intent.repName ?? undefined,
                managerName: intent.managerName ?? undefined,
                srManagerName: intent.srManagerName ?? undefined,
                latLong: `${leadGeo?.latitude ?? cachedLeadGeo?.latitude}` +
                    "_" +
                    `${leadGeo?.longitude ?? cachedLeadGeo?.longitude}`,
            };
            const resolvedLeadIntent = {
                ...(leadIntent ?? {}),
                ...(schoolSession?.prospectId?.trim()
                    ? {
                        searchBy: "prospect_id",
                        prospect_id: schoolSession.prospectId.trim(),
                    }
                    : {}),
                city: leadIntent?.city?.trim() || enrichedIntent.city || undefined,
                state: leadIntent?.state?.trim() || enrichedIntent.state || undefined,
                mxCertificate: truncateGeniusLsqUrl(leadIntent?.mxCertificate ?? intent.certificateUrl),
                mxCarLink: truncateGeniusLsqUrl(leadIntent?.mxCarLink ?? intent.reportUrl),
                mxTestDate: leadIntent?.mxTestDate ?? intent.testDate ?? undefined,
            };
            const schema = remoteData.schemas?.InMobiusOnlineTestEventId;
            const merged = mergeInMobiusOnlineTestIntent(enrichedIntent);
            const schoolCode = schoolDetails?.school_code?.trim();
            const geniusToken = readGeniusLsqAccessToken();
            if (!intent.schoolFees?.trim()) {
                console.log("[SqsEvents] schoolFees not on intent, fallback lookup", {
                    hasSchoolCode: Boolean(schoolCode),
                    hasSessionToken: Boolean(geniusToken),
                });
            }
            const schoolFees = intent.schoolFees?.trim() ||
                (schoolCode && geniusToken
                    ? ((await fetchGeniusSchoolCategoryDetails({
                        schoolCode,
                        accessToken: geniusToken,
                        platform: resolveLsqServicePlatform(),
                    })) ?? undefined)
                    : undefined);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.testName, merged.testName)
                .put(schema?.mode, merged.mode)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.grade, merged.grade)
                .put(schema?.username, merged.username)
                .put(schema?.source, merged.source)
                .put(schema?.schoolName, merged.schoolName)
                .put(schema?.city, merged.city)
                .put(schema?.district, merged.district)
                .put(schema?.state, merged.state)
                .put(schema?.country, merged.country)
                .put(schema?.schoolFees ?? "mx_Custom_12", schoolFees)
                .put(schema?.platform, merged.platform)
                .put(schema?.percentage, merged.percentage)
                .put(schema?.marksObtained, merged.marksObtained)
                .put(schema?.totalMarks, merged.totalMarks)
                .put(schema?.correctAnswers, merged.correctAnswers)
                .put(schema?.incorrectAnswers, merged.incorrectAnswers)
                .put(schema?.questionsAnswered, merged.questionsAnswered)
                .put(schema?.reportUrl, merged.reportUrl)
                .put(schema?.certificateUrl ?? "mx_Custom_21", merged.certificateUrl)
                .put(schema?.round, merged.round)
                .put(schema?.examType, merged.examType)
                .put(schema?.repName ?? "mx_Custom_24", merged.repName)
                .put(schema?.managerName ?? "mx_Custom_25", merged.managerName)
                .put(schema?.srManagerName ?? "mx_Custom_26", merged.srManagerName)
                .put(schema?.ipAddress, merged.ipAddress)
                .put(schema?.latLong, merged.latLong)
                .put(schema?.employeeCode ?? "mx_Custom_29", merged.employeeCode)
                .build();
            console.log("jsonObject", jsonObject);
            const pickField = (name) => jsonObject.find((field) => field.SchemaName === name)?.Value ??
                "MISSING";
            console.log("[SqsEvents] InMobiusOnlineTest key fields", {
                mx_Custom_12_schoolFees: pickField("mx_Custom_12"),
                mx_Custom_20_reportUrl: pickField("mx_Custom_20"),
                mx_Custom_21_certificateUrl: pickField("mx_Custom_21"),
                mx_Custom_29_employeeCode: pickField("mx_Custom_29"),
            });
            const eventId = remoteData.eventIds?.InMobiusOnlineTestEventId;
            await emitGeniusActivity(eventId, jsonObject, resolvedLeadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] inMobiusOnlineTest:", e);
            }
        }
    })();
}
function geniusPhoneLink(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            const leadGeo = await ensureLeadGeoCached();
            const schema = remoteData.schemas?.GeniusPhoneLink;
            const merged = mergeGeniusPhoneLinkIntent(intent);
            const schoolSession = readGeniusSchoolDetailsSession();
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.loginUrl, merged.loginUrl)
                .put(schema?.source, merged.source)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .build();
            console.log("merged", merged);
            const eventId = remoteData.eventIds?.GeniusPhoneLink;
            const localLead = {
                ...leadIntent,
                firstName: intent.firstName ?? merged.firstName ?? undefined,
                lastName: intent.lastName ?? merged.lastName ?? undefined,
                userPhoneNumber: intent.phoneNumber ?? merged.phoneNumber ?? undefined,
                unifiedAccountID: leadIntent?.unifiedAccountID ?? merged.unifiedAccountId ?? undefined,
                mxStudentLoginID: initOptions?.getLoginUser()?.studentLoginId ?? undefined,
                primaryTargetExam: initOptions?.getLoginUser()?.profile?.exams?.[0]?.name ?? undefined,
                mxInmobiusSchoolCode: schoolSession?.schoolDetails?.school_code ?? undefined,
                schoolName: schoolSession?.schoolDetails?.school_name ?? undefined,
                mxInmobiusSchoolAddress: schoolSession?.schoolDetails?.address ?? undefined,
                mxDistrict: schoolSession?.schoolDetails?.district_name?.trim() || undefined,
                centerPinCode: schoolSession?.schoolDetails?.postal_code?.trim() || undefined,
                city: leadIntent?.city?.trim() ||
                    leadGeo?.city?.trim() ||
                    cachedLeadGeo?.city?.trim() ||
                    undefined,
                state: leadIntent?.state?.trim() ||
                    leadGeo?.region?.trim() ||
                    cachedLeadGeo?.region?.trim() ||
                    undefined,
                country: leadIntent?.country?.trim() ||
                    leadGeo?.country?.trim() ||
                    cachedLeadGeo?.country?.trim() ||
                    undefined,
                pageUrl: merged.loginUrl ?? truncateGeniusLsqUrl(leadIntent?.pageUrl),
            };
            console.log("schoolSession", schoolSession);
            await emitGeniusActivity(eventId, jsonObject, localLead);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] geniusPhoneLink:", e);
            }
        }
    })();
}
export const SqsEvents = {
    init,
    actMarketingActivity,
    actTests,
    actMasterClass,
    actSelfLearnStory,
    actSelfLearnFlashcard,
    actSelfLearnVideo,
    actTalkToExpertActivity,
    actDoubtActivity,
    actSignUp,
    appScoreRegistration,
    appAddToCart,
    geniusUserLogin,
    inMobiusOnlineTest,
    geniusPhoneLink,
    getUtcDateTime,
};
